import { Controller } from "@hotwired/stimulus"
import { hold, saveLayout } from "../lib/layout_preferences"

// Drag / keyboard handles between the inbox panels (FRO-138): sidebar | list | thread | rail.
//
// Every width is a custom property on .app (--sidebar-width, --list-width, --rail-width) and the
// collapse states are classes on it — the same place the server renders them from
// users.ui_preferences, so first paint, a reload on another browser and a morph refresh all agree
// with what this controller last saved. application.scss clamps each property into its range and
// against the viewport, so this only has to keep its own numbers honest for the handles' aria.
//
// The rail's handle and toggle live inside the "conversation" frame and are swapped with every
// thread; they are targets of this controller on .app, so they pick the current state up again on
// connect rather than carrying any of their own.
//
// Sidebar collapse goes through sidebar-collapse, which owns the toggle button and exists on the
// settings and report shells too.
const STEP = 16
const BIG_STEP = 64
const KEY_SAVE_DELAY = 400

export default class extends Controller {
  static targets = ["handle", "railToggle"]
  static values = { panels: Object, threadMin: Number, url: String }

  // initialize rather than connect: Stimulus fires handleTargetConnected before connect, and the
  // handles need the widths to say their aria values.
  initialize() {
    this.widths = {}
    for (const panel of Object.keys(this.panelsValue)) {
      const inline = parseInt(this.element.style.getPropertyValue(`--${panel}-width`), 10)
      this.widths[panel] = Number.isFinite(inline) ? inline : this.panelsValue[panel].default
    }
  }

  connect() {
    this.onWindowResize = () => this.#syncHandles()
    window.addEventListener("resize", this.onWindowResize)
  }

  disconnect() {
    window.removeEventListener("resize", this.onWindowResize)
    this.#finishDrag()
    this.#flushKeySave()
  }

  handleTargetConnected(handle) {
    this.#syncHandle(handle)
  }

  railToggleTargetConnected(button) {
    button.setAttribute("aria-pressed", String(this.#collapsed("rail")))
  }

  // ---- pointer ----

  start(event) {
    if (event.button !== 0 || this.drag) return
    const handle = event.currentTarget
    const panel = handle.dataset.panel
    event.preventDefault()
    handle.focus({ preventScroll: true })
    // Capture keeps the cursor and hover off whatever the pointer crosses; the moves themselves are
    // read from window, so a capture the browser refuses (a synthetic pointer) still drags.
    try { handle.setPointerCapture(event.pointerId) } catch {}

    this.drag = {
      handle,
      panel,
      pointerId: event.pointerId,
      startX: event.clientX,
      x: event.clientX,
      startWidth: this.#collapsed(panel) ? this.panelsValue[panel].collapsed : this.#effectiveWidth(panel),
      before: { width: this.widths[panel], collapsed: this.#collapsed(panel) },
      release: hold()
    }
    this.element.classList.add("is-resizing")

    this.onMove = (e) => this.#move(e)
    this.onUp = () => this.#endDrag(true)
    // The browser took the pointer away (Chrome does this on Escape, before the keydown): the drag
    // did not finish, so it does not count.
    this.onCancel = () => this.#cancelDrag()
    this.onEscape = (e) => {
      if (e.key !== "Escape") return
      e.preventDefault()
      e.stopPropagation()
      this.#cancelDrag()
    }
    window.addEventListener("pointermove", this.onMove)
    window.addEventListener("pointerup", this.onUp)
    window.addEventListener("pointercancel", this.onCancel)
    window.addEventListener("keydown", this.onEscape, true)
  }

  #move(event) {
    if (!this.drag || event.pointerId !== this.drag.pointerId) return
    this.drag.x = event.clientX
    if (this.frame) return
    this.frame = requestAnimationFrame(() => {
      this.frame = null
      if (!this.drag) return
      const { panel, startX, x, startWidth } = this.drag
      // The rail's handle sits on its LEFT edge, so it grows as the pointer moves left.
      const delta = panel === "rail" ? startX - x : x - startX
      this.#dragTo(panel, startWidth + delta)
    })
  }

  #dragTo(panel, raw) {
    const config = this.panelsValue[panel]
    if (config.collapse_below && raw < config.collapse_below) {
      // Reopening restores the width it had before this drag, not whatever it passed through.
      this.#setWidth(panel, this.drag.before.width)
      this.#setCollapsed(panel, true, { save: false })
    } else {
      this.#setCollapsed(panel, false, { save: false })
      this.#setWidth(panel, raw)
    }
  }

  #cancelDrag() {
    if (!this.drag) return
    this.#restore()
    this.#endDrag(false)
  }

  #endDrag(save) {
    const drag = this.drag
    if (!drag) return
    this.#finishDrag()
    if (!save) return
    const { panel, before } = drag
    // A plain click on the handle is not a resize; do not write anything for it.
    if (before.width === this.widths[panel] && before.collapsed === this.#collapsed(panel)) return
    this.#save(panel)
  }

  #finishDrag() {
    const drag = this.drag
    if (!drag) return
    this.drag = null
    if (this.frame) cancelAnimationFrame(this.frame)
    this.frame = null
    window.removeEventListener("pointermove", this.onMove)
    window.removeEventListener("pointerup", this.onUp)
    window.removeEventListener("pointercancel", this.onCancel)
    window.removeEventListener("keydown", this.onEscape, true)
    if (drag.handle.hasPointerCapture?.(drag.pointerId)) drag.handle.releasePointerCapture(drag.pointerId)
    this.element.classList.remove("is-resizing")
    drag.release()
  }

  #restore() {
    const { panel, before } = this.drag
    this.#setWidth(panel, before.width)
    this.#setCollapsed(panel, before.collapsed, { save: false })
  }

  // ---- double-click / keyboard / toggle ----

  reset(event) {
    const panel = event.currentTarget.dataset.panel
    this.#setCollapsed(panel, false, { save: false })
    this.#setWidth(panel, this.panelsValue[panel].default)
    this.#save(panel)
  }

  key(event) {
    if (this.drag) return
    const panel = event.currentTarget.dataset.panel
    const config = this.panelsValue[panel]
    const sign = panel === "rail" ? -1 : 1
    const step = event.shiftKey ? BIG_STEP : STEP
    let width

    switch (event.key) {
      case "ArrowRight": width = this.#effectiveWidth(panel) + sign * step; break
      case "ArrowLeft": width = this.#effectiveWidth(panel) - sign * step; break
      case "Home": width = config.min; break
      case "End": width = this.#max(panel); break
      case "Enter":
        if (!config.collapse_below) return
        event.preventDefault()
        event.stopPropagation()
        this.#setCollapsed(panel, !this.#collapsed(panel))
        return
      default: return
    }

    event.preventDefault()
    event.stopPropagation()
    this.#setCollapsed(panel, false, { save: false })
    this.#setWidth(panel, width)
    this.#queueKeySave(panel)
  }

  toggleRail() {
    this.#setCollapsed("rail", !this.#collapsed("rail"))
  }

  // sidebar-collapse's own toggle moves the room the list and rail have.
  sync() {
    this.#syncHandles()
  }

  // ---- state ----

  #setWidth(panel, width) {
    const config = this.panelsValue[panel]
    this.widths[panel] = Math.round(Math.min(Math.max(width, config.min), this.#max(panel)))
    this.element.style.setProperty(`--${panel}-width`, `${this.widths[panel]}px`)
    this.#syncHandles()
  }

  #setCollapsed(panel, collapsed, { save = true } = {}) {
    if (this.#collapsed(panel) === collapsed) return

    if (panel === "sidebar") {
      this.#sidebarCollapse?.set(collapsed, { save })
      if (!this.#sidebarCollapse) this.element.classList.toggle("is-sidebar-collapsed", collapsed)
    } else if (panel === "rail") {
      this.element.classList.toggle("is-rail-collapsed", collapsed)
      this.railToggleTargets.forEach((button) => button.setAttribute("aria-pressed", String(collapsed)))
      if (save) saveLayout(this.urlValue, { rail_collapsed: collapsed })
    }
    this.#syncHandles()
  }

  #collapsed(panel) {
    return this.element.classList.contains(`is-${panel}-collapsed`)
  }

  #save(panel) {
    const attributes = { [`${panel}_width`]: this.widths[panel] }
    if (this.panelsValue[panel].collapse_below) attributes[`${panel}_collapsed`] = this.#collapsed(panel)
    saveLayout(this.urlValue, attributes)
  }

  #queueKeySave(panel) {
    if (this.keySave && this.keySave.panel !== panel) this.#flushKeySave()
    if (this.keySave) {
      clearTimeout(this.keySave.timer)
    } else {
      this.keySave = { panel, release: hold() }
    }
    this.keySave.timer = setTimeout(() => this.#flushKeySave(), KEY_SAVE_DELAY)
  }

  #flushKeySave() {
    const pending = this.keySave
    if (!pending) return
    this.keySave = null
    clearTimeout(pending.timer)
    this.#save(pending.panel)
    pending.release()
  }

  get #sidebarCollapse() {
    return this.application.getControllerForElementAndIdentifier(this.element, "sidebar-collapse")
  }

  // ---- geometry: the same arithmetic as the clamp()s in application.scss ----

  #track(panel) {
    return this.#collapsed(panel) ? this.panelsValue[panel].collapsed : this.#effectiveWidth(panel)
  }

  #effectiveWidth(panel) {
    const config = this.panelsValue[panel]
    return Math.min(Math.max(this.widths[panel], config.min), this.#max(panel))
  }

  // The thread keeps threadMin: the rail gives way first against the sidebar and the list's
  // minimum, then the list against everything else. Never below a panel's own minimum.
  #max(panel) {
    const config = this.panelsValue[panel]
    const viewport = window.innerWidth
    let room = Infinity
    if (panel === "rail") {
      room = viewport - this.#track("sidebar") - this.panelsValue.list.min - this.threadMinValue
    } else if (panel === "list") {
      room = viewport - this.#track("sidebar") - this.#track("rail") - this.threadMinValue
    }
    return Math.max(config.min, Math.min(config.max, room))
  }

  #syncHandles() {
    this.handleTargets.forEach((handle) => this.#syncHandle(handle))
  }

  #syncHandle(handle) {
    const panel = handle.dataset.panel
    const config = this.panelsValue[panel]
    if (!config) return
    handle.setAttribute("aria-valuemin", config.min)
    handle.setAttribute("aria-valuemax", this.#max(panel))
    // valuenow stays the width it reopens to, inside min..max; the collapse is said in valuetext.
    handle.setAttribute("aria-valuenow", this.#effectiveWidth(panel))
    if (this.#collapsed(panel)) {
      handle.setAttribute("aria-valuetext", "Collapsed")
    } else {
      handle.removeAttribute("aria-valuetext")
    }
  }
}
